import { Button } from '@/components/UI/Button'
import { Dialog } from '@/components/UI/Dialog'
import { HtmlContent } from '@/components/UI/HtmlContent'
import { IconAlertTriangle, IconX as IconClose } from '@tabler/icons-react'
import { useRouter } from 'next/router'
import type React from 'react'
import { memo } from 'react'

export type ActionDialogVariant = 'action-positive' | 'action-negative' | 'action-icon' | 'message'

type Props = {
  children?: React.ReactNode
  title?: string
  /** タイトルを小さめのフォントで表示 */
  titleSmall?: boolean
  /** タイトル下の本文テキスト */
  description?: string
  /** タイトル下のサブテキスト（小さめの説明文） */
  subtext?: string
  image?: string
  selectorState?: boolean
  closeAction?: () => void
  closeFunc?: () => void
  reloadOnClose?: boolean
  /**
   * Figma定義のバリアント。指定するとコンパクトポップアップとして表示し、
   * variant に応じたボタンレイアウトを自動レンダリングする。
   * - action-positive: [キャンセル] [アクション(黄色)]
   * - action-negative: [アクション(赤)] [キャンセル]
   * - action-icon: アイコン付き + [アクション(黄色・中央)]
   * - message: [OK(グレー・中央)]
   */
  variant?: ActionDialogVariant
  /** アクションボタンのラベル (default: "アクション") */
  confirmLabel?: string
  /** キャンセル/閉じるボタンのラベル (default: variant依存) */
  cancelLabel?: string
  /** アクションボタンクリック時のコールバック */
  onConfirm?: () => void
  /** キャンセルボタンクリック時のコールバック */
  onCancel?: () => void
  /** ActionDialog/Icon で表示するアイコン (指定しない場合はデフォルトの警告アイコン) */
  icon?: React.ReactNode
  /** コンパクトポップアップモード（variant指定時は自動でtrue） */
  compact?: boolean
  /** Mantine Modal の z-index を上書きする。高い z-index を持つ要素の上に表示する場合に使用 */
  zIndex?: number
}

export const ActionDialog = memo(
  ({
    children,
    title,
    titleSmall = false,
    description,
    subtext,
    image,
    selectorState,
    closeAction,
    closeFunc,
    reloadOnClose,
    variant,
    confirmLabel,
    cancelLabel,
    onConfirm,
    onCancel,
    icon,
    compact,
    zIndex,
  }: Props) => {
    const router = useRouter()

    const handleReloadClose = () => {
      if (reloadOnClose) router.reload()
    }

    const handleCancel = () => {
      onCancel?.()
    }

    const isCompact = compact || !!variant

    const defaultCancelLabel = variant === 'message' ? 'OK' : 'キャンセル'
    const resolvedCancelLabel = cancelLabel ?? defaultCancelLabel
    const resolvedConfirmLabel = confirmLabel ?? 'アクション'

    const WarningIcon = () => (
      <IconAlertTriangle size={64} stroke={1.5} color="var(--mantine-color-primary-5)" aria-hidden="true" />
    )

    const InlineCloseIcon = () => <IconClose size={16} stroke={2} aria-hidden="true" />

    return (
      <Dialog
        selectorState={selectorState}
        compact={isCompact}
        closeAction={() => {
          closeAction?.()
          handleReloadClose()
        }}
        closeFunc={closeFunc}
        zIndex={zIndex}
        showCloseButton={!variant && !compact}
      >
        <div>
          {variant === 'action-icon' && (
            <>
              <button
                type="button"
                className="c-dialog-close c-dialog-close--inline"
                onClick={() => {
                  closeAction?.()
                  handleReloadClose()
                }}
                aria-label="閉じる"
              >
                <InlineCloseIcon />
              </button>
              <div className="c-dialog-icon-area">{icon ?? <WarningIcon />}</div>
            </>
          )}

          {/* TODO: Dialogにタイトル無しでアイコンを付与できるようにする */}
          {title && (
            <HtmlContent
              html={title}
              className={`text-gray-600 text-center ${titleSmall ? 'text-h2' : 'text-h1'} font-bold mt-2`}
            />
          )}

          {image && (
            <div className="mx-auto mt-5 h-24 w-24 rounded-full text-center">
              <img src={`${process.env.NEXT_PUBLIC_SITEURL}/images/img_demo_stamp.png`} alt={image} />
            </div>
          )}

          {description && (
            <HtmlContent html={description} className="text-gray-600 mt-4 text-center text-sm font-bold" />
          )}

          {subtext && <HtmlContent html={subtext} className="c-dialog-subtext" />}

          {!!children && children}

          {variant === 'action-positive' && (
            <div className="c-dialog-button-area">
              <Button
                label={resolvedCancelLabel}
                variant="system"
                size="md"
                fullWidth
                onClick={() => {
                  handleCancel()
                  closeAction?.()
                }}
              />
              <Button label={resolvedConfirmLabel} variant="primary" size="md" fullWidth onClick={onConfirm} />
            </div>
          )}

          {variant === 'action-negative' && (
            <div className="c-dialog-button-area">
              <Button label={resolvedConfirmLabel} variant="danger" size="md" fullWidth onClick={onConfirm} />
              <Button
                label={resolvedCancelLabel}
                variant="system-back"
                size="md"
                fullWidth
                onClick={() => {
                  handleCancel()
                  closeAction?.()
                }}
              />
            </div>
          )}

          {(variant === 'action-icon' || variant === 'message') && (
            <div className="c-dialog-button-area c-dialog-button-area--single">
              <Button
                label={variant === 'action-icon' ? resolvedConfirmLabel : resolvedCancelLabel}
                variant={'primary'}
                size="md"
                fullWidth
                onClick={
                  variant === 'action-icon'
                    ? onConfirm
                    : () => {
                        handleCancel()
                        closeAction?.()
                      }
                }
              />
            </div>
          )}
        </div>
      </Dialog>
    )
  },
)
